import { type ClassValue, clsx } from "clsx";
import { set } from "date-fns";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import { getCurrentFortnight } from "@/components/reusables/FortnightPicker";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { FullName, UserProps } from "@/interfaces/interfaces";

/**
 * @param {...any} inputs - list of classes
 * @description this is cn to use tailwind classes
 * @returns {string} - returns the string of classes
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export const isAdminDomain = window.location.hostname.split(".")[0] === "admin";
export const isSubDomain =
  localStorage.getItem("subdomain") !== "admin" &&
  localStorage.getItem("subdomain") !== "customer";
export const isCustomerPortal =
  localStorage.getItem("subdomain") === "customer";

export const getRootUserId = (): string => {
  const userData = JSON.parse(
    localStorage.getItem("userData") || "",
  ) as UserProps;

  return userData?.id ?? "-";
};

/**
 * @description it converts enum to object
 * @param {object} enumObj - enum object
 * @returns {{key: string, value: string}[]} - array of enum values
 */
export const enumToObj = (
  enumObj: object,
): {
  key: string;
  value: string;
}[] => {
  return Object.keys(enumObj).map((key) => ({
    key,
    value: enumObj[key as keyof typeof enumObj],
  }));
};

/**
 * @description it converts enum to array
 * @param {object} enumObj - enum object
 * @returns {string[]} - array of enum values
 */
export const enumToArray = (enumObj: object): string[] => {
  return Object.keys(enumObj)
    .map((key) => enumObj[key as keyof typeof enumObj])
    .slice(0, Object.keys(enumObj).length / 2);
};

type ArrayOrObject<T> = T[] | Record<string, T[]> | null | undefined;
/**
 * @description it returns the display value of an array or object
 * @param {ArrayOrObject<T> } input - input array or object
 * @param {boolean} isUnique - flag to check if the values are unique
 * @returns {string} - display value
 */
export function getDisplayValue<T>(
  input: ArrayOrObject<T>,
  isUnique: boolean = false,
): string {
  if (!input) {
    return "";
  }

  let values: T[] = [];

  if (Array.isArray(input)) {
    values = input.filter(Boolean);
  } else {
    values = Object.values(input).flat().filter(Boolean);
  }

  if (values.length === 0) {
    return "";
  }

  if (isUnique && values.length > 1) {
    const uniqueValues = new Set(values);

    return uniqueValues.size > 1
      ? "Multiple"
      : (Array.from(uniqueValues)[0] as unknown as string);
  }

  return values.length > 1
    ? "Multiple"
    : ((values[0] as unknown as string) ?? "");
}

type Props = string | number | null | undefined;

/**
 * @description Extracts the data for sorting.
 * @param {Props} value - Input value
 * @returns {string | number | undefined} - Extracted value for sorting
 */
export const extractSortData = (value: Props): string | number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  if (Number.isInteger(value)) {
    return Number(value);
  }

  if (typeof value === "string") {
    return value.toLowerCase();
  }

  return value;
};

/**
 * @description Converts the size in bytes to KB
 * @param {number} size - size in bytes
 * @returns {string} - size in KB
 */
export const formatSizeToKB = (size: number): string => {
  return `${(size / 1024).toFixed(2)} KB`;
};

export const DateStringToZeroDate = (date: string): string => {
  const formattedDate = new Date(
    new Date(date).setHours(0, 0, 0, 0),
  ).toISOString();

  return formattedDate || new Date().toISOString();
};

export const formatToCapitalizedWords = (str: string): string => {
  // Handle snake_case
  if (str.includes("_")) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  // Handle camelCase/PascalCase
  const result = str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between a lowercase and uppercase letter
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2"); // Separate acronyms from words starting with uppercase

  return result
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const countWordsLength = (str: string): number => {
  if (!str || str.trim().length === 0) {
    return 0;
  }

  // Remove extra whitespace and split into words
  const words = str
    .trim()
    // Replace multiple spaces with single space
    .replace(/\s+/g, " ")
    // Replace common punctuation with spaces
    .replace(/[.,!?;:()[\]{}""''`]/g, " ")
    // Split on remaining whitespace
    .split(/\s+/);

  return words.length;
};

export interface RenderFullNameProps {
  userData?: Partial<UserProps> | null;
  params?: FullName;
}
/**
 * Renders the full name of a user by concatenating their first, middle, and last names.
 *
 * If the `params` object is provided, it takes precedence over `userData` for constructing the name.
 * Any undefined or empty parts (firstName, middleName, or lastName) will be ignored in the final output.
 * @param {object} options - The input options.
 * @param {object} [options.userData] - The user data containing name fields.
 * @param {string} [options.userData.firstName] - The user's first name.
 * @param {string} [options.userData.middleName] - The user's middle name.
 * @param {string} [options.userData.lastName] - The user's last name.
 * @param {object} [options.params] - The name parameters to override `userData`.
 * @param {string} [options.params.firstName] - The override for the user's first name.
 * @param {string} [options.params.middleName] - The override for the user's middle name.
 * @param {string} [options.params.lastName] - The override for the user's last name.
 * @returns {string} The concatenated full name, or an empty string if no valid name parts are provided.
 */

export const renderFullName = ({
  userData = null,
  params,
}: RenderFullNameProps): string => {
  // If both `userData` and `params` are null/undefined, return an empty string.
  if (!userData && !params) {
    return "";
  }

  // Use `params` if provided, overriding `userData`.
  if (params) {
    const { firstName = "", middleName = "", lastName = "" } = params;
    const fullName = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(" ");

    return fullName || ""; // Ensure an empty string if all parts are blank.
  }

  // Fallback to `userData` if `params` is not provided.
  if (userData) {
    const { firstName = "", middleName = "", lastName = "" } = userData;
    const fullName = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(" ");

    return fullName || ""; // Ensure an empty string if all parts are blank.
  }

  // Default to an empty string (should never reach here due to earlier checks).
  return "";
};

/**
 * Downloads a PDF file from the given URL and saves it with the specified filename.
 * @param {string} url - The URL of the PDF file to download.
 * @param {string} [filename] - The name of the file to save locally. Defaults to "download.pdf".
 * @returns {void}
 * @example
 * // Downloads a PDF with the name "myFile.pdf"
 * downloadPDF("https://example.com/sample.pdf", "myFile.pdf");
 */
export const downloadPDF = (
  url: string,
  filename: string = "download.pdf",
): void => {
  fetch(url)
    .then((response: Response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch the file: ${response.statusText}`);
      }

      return response.blob();
    })
    .then((blob: Blob) => {
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobURL;
      anchor.download = filename; // Sets the file's download name
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor); // Clean up DOM
      URL.revokeObjectURL(blobURL); // Release memory
    })
    .catch((error: Error) => {
      toast.error(error.message);
    });
};

interface FormatWithPadStartProps {
  value: string | number;
  prefix?: string;
  length: number;
  padChar?: string;
}

/**
 * Adds a prefix and pads the input value to a specified length.
 * @param {string | number} value - The value to be padded.
 * @returns {string} A formatted string with the prefix and padded value.
 * @example formatWithPadStart(123, "INV-", 6); // Returns "INV-000123"
 * @example formatWithPadStart("ABC", "ID-", 5, "-"); // Returns "ID--ABC"
 */
export function formatWithPadStart({
  value,
  prefix = "0",
  length,
  padChar = "0",
}: FormatWithPadStartProps): string {
  const stringValue = String(value); // Convert the input to a string
  const paddedValue = stringValue.padStart(length, padChar); // Pad the value

  return `${prefix}${paddedValue}`; // Combine prefix and padded value
}

//Function to filter columns based on key
export const filterColumns = <T>({
  columns,
  hiddenColumnsHeaders,
}: {
  columns: ExtendedColumnDef<T>[];
  hiddenColumnsHeaders: string[];
}): ExtendedColumnDef<T>[] =>
  hiddenColumnsHeaders.length === 0
    ? columns
    : columns.filter(
        (column) =>
          !column.header ||
          !hiddenColumnsHeaders.includes(column.header as string),
      );

/**
 * Generates a query string for the current fortnight's start and end dates.
 * @returns {string} - Query string in the format `startDate=<startDate>&endDate=<endDate>`
 */
export const getCurrentFortnightQuery = (): string => {
  // Get the current fortnight
  const currentFortnightdate = getCurrentFortnight();

  // Format the start and end dates
  const startDate = set(new Date(currentFortnightdate.start), {
    hours: 0,
    minutes: 0,
    seconds: 0,
  }).toISOString();

  const endDate = set(new Date(currentFortnightdate.end), {
    hours: 23,
    minutes: 59,
    seconds: 59,
  }).toISOString();

  // Return the query string
  return `startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
};

/**
 * Formats a file size in bytes into a human-readable string representation.
 *
 * @param {number} sizeInBytes - The size of the file in bytes.
 * @param {boolean} isFolder - A boolean indicating whether the item is a folder.
 *                   If true, the function will return a placeholder ("-").
 * @returns {string} A string representing the file size in either KB or MB.
 *          If the size is 0 bytes, it returns "0 KB".
 *          If the size is 1 MB or greater, it is formatted in MB with two decimal places.
 *          Otherwise, it is formatted in KB with two decimal places.
 */
export const formatFileSize = (
  sizeInBytes: number,
  isFolder: boolean,
): string => {
  if (isFolder) {
    return "-";
  }
  if (sizeInBytes === 0) {
    return "0 KB";
  }

  const sizeInMb = sizeInBytes / 1024 / 1024;
  const sizeInKb = sizeInBytes / 1024;

  return sizeInMb >= 1
    ? `${sizeInMb.toFixed(2)} MB`
    : `${sizeInKb.toFixed(2)} KB`;
};

/**
 * Recursively replaces all undefined values in an object with null.
 * This is useful for preparing data for JSON serialization, as undefined values
 * are omitted during serialization while null values are preserved.
 *
 * @template T - The type of the input object
 * @param {T} obj - The object to transform
 * @returns {T} A new object with all undefined values replaced by null
 *
 * @example
 * const data = { a: 1, b: undefined, c: { d: undefined } };
 * const sanitized = replaceUndefinedWithNull(data);
 * // Returns { a: 1, b: null, c: { d: null } }
 */
export function replaceUndefinedWithNull<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(replaceUndefinedWithNull) as unknown as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

export const removeHyphens = (str: string): string | null => {
  if (!str) {
    return null;
  }

  return str.replace(/-/g, "");
};

export type TCurrency = "USD" | "AUD" | "NPR";
export const getCurrencySymbol = (currency: TCurrency): string => {
  const currencySymbols: Record<TCurrency, string> = {
    USD: "$",
    AUD: "A$",
    NPR: "Rs ",
  };

  return currencySymbols[currency];
};
